import { useQuery, useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { preferencesData } from './data';
import { CreateCareerLevelPreferenceDocument } from './mutations/create-career-level-preference.mutation.gql-types';
import { CreateSalaryExpectationsPreferenceDocument } from './mutations/create-salary-expectations-preference.mutation.gql-types';
import { CreateWorkplacePreferenceDocument } from './mutations/create-workplace-preference.mutation.gql-types';
import { ReadLoggedOutPreferencesDocument } from './mutations/read-preferences.query.gql-types';
import type { Preferences } from './types';
import { getPreferenceKey } from './utils';

export function useReadLoggedOutPreferences(): {
  loading: boolean;
  preferences: Preferences | null;
} {
  const { data, loading } = useQuery(ReadLoggedOutPreferencesDocument, {
    ssr: false,
  });

  const result = useMemo(() => {
    if (loading || !data) return { loading, preferences: null };
    let workplacePreference = '';

    if (data.viewer?.workplacePreference?.office) {
      workplacePreference = 'office';
    } else if (data.viewer?.workplacePreference?.partlyHome) {
      workplacePreference = 'partlyHome';
    } else if (data.viewer?.workplacePreference?.mostlyHome) {
      workplacePreference = 'mostlyHome';
    } else if (data.viewer?.workplacePreference?.homeOffice) {
      workplacePreference = 'homeOffice';
    }

    return {
      loading,
      preferences: {
        preferred_workplace: getPreferenceKey(
          'preferred_workplace',
          workplacePreference
        ),
        career_level: getPreferenceKey(
          'career_level',
          data.viewer?.careerLevelPreference?.minimum?.toString()
        ),
        salary_expectations: getPreferenceKey(
          'salary_expectations',
          data.viewer?.salaryExpectationsPreference?.salaryExpectations?.toString()
        ),
      },
    };
  }, [loading, data]);

  return result;
}

type CreateLoggedOutPreferencesResult = {
  create: (key: string, value: string, oldValue?: string) => void;
  loading: boolean;
};

export function useCreateLoggedOutPreference(): CreateLoggedOutPreferencesResult {
  const [
    createSalaryExpectationsPreference,
    { loading: loadingSalaryExpectations },
  ] = useMutation(CreateSalaryExpectationsPreferenceDocument);
  const [createWorkplacePreference, { loading: loadingWorkplaceExpectations }] =
    useMutation(CreateWorkplacePreferenceDocument);
  const [createCareerLevelPreference, { loading: loadingCareerLevel }] =
    useMutation(CreateCareerLevelPreferenceDocument);

  const createFn = useCallback(
    (key: string, value: string) => {
      const preferenceData = preferencesData.options.find(
        (preference) => preference.preference === key
      );

      if (!preferenceData) return;

      if (key === 'salary_expectations') {
        createSalaryExpectationsPreference({
          variables: {
            input: {
              salaryExpectations: parseInt(
                preferenceData.possibleValues[value]
              ),
            },
          },
        });
      } else if (key === 'career_level') {
        createCareerLevelPreference({
          variables: {
            input: { minimum: parseInt(preferenceData.possibleValues[value]) },
          },
        });
      } else if (key === 'preferred_workplace') {
        createWorkplacePreference({
          variables: {
            input: {
              office: preferenceData.possibleValues[value] === 'office',
              partlyHome: preferenceData.possibleValues[value] === 'partlyHome',
              mostlyHome: preferenceData.possibleValues[value] === 'mostlyHome',
              homeOffice: preferenceData.possibleValues[value] === 'homeOffice',
            },
          },
        });
      }
    },
    [
      createSalaryExpectationsPreference,
      createCareerLevelPreference,
      createWorkplacePreference,
    ]
  );

  return {
    create: createFn,
    loading:
      loadingSalaryExpectations ||
      loadingWorkplaceExpectations ||
      loadingCareerLevel,
  };
}
